import { useState } from 'react';
import { useLocation } from 'react-router';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import Logout from '@mui/icons-material/Logout';
import NightlightOutlinedIcon from '@mui/icons-material/NightlightOutlined';
import {
  AppBar,
  Avatar,
  Box,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import { useAuthContext } from 'src/auth/useAuthContext';
import { createGlassmorphismStyles } from 'src/components/GlassmorphismStyles';
import Iconify from 'src/components/iconify/Iconify';
import useResponsive from 'src/hooks/useResponsive';
import { useSettings } from 'src/hooks/useSettings';
import { isMobile } from 'src/utils/common';

import Logo from '../components/logo';
import { HEADER } from '../config-global';

import navConfig from './dashboard/nav/config-navigation';
import NavDesktop from './dashboard/nav/desktop';
import NavVertical from './dashboard/nav/NavVertical';
import { NavItemProps } from './dashboard/nav/types';

const Header = observer(() => {
  const theme = useTheme();

  const { pathname } = useLocation();
  const { themeMode, onChangeMode } = useSettings();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const isMd = useResponsive('down', 'md');
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const { user, logout: authLogout } = useAuthContext();
  const logout = async () => {
    await authLogout();
  };

  const [menuOpen, setMenuOpen] = useState(false);

  let title;
  navConfig.forEach((item: NavItemProps) => {
    if (pathname === '/') {
      title = '월간 리포트';
    }
    if (pathname === '/my/info') {
      title = '내정보 수정';
    }
    if (pathname === item.path) {
      title = item?.enTitle ? item.enTitle : item.title;
    }
    if (item.children) {
      item.children.forEach((child) => {
        if (pathname === child.path) {
          title = child?.enTitle ? child.enTitle : child.title;
        }
      });
    }
  });

  return (
    <>
      <AppBar
        color="default"
        sx={{
          display: 'flex',
          width: 'calc(100% - 32px)',
          left: '50%',
          transform: 'translateX(-50%)',
          borderRadius: '0 0 30px 30px',
          px: 2,
          ...createGlassmorphismStyles(theme, {
            interactive: false,
            intensity: 'light',
            elevation: 'low',
          }),
          backdropFilter: 'multiply saturate(44%)',
          webkitBackdropFilter: 'multiply saturate(44%)',
        }}
      >
        <Toolbar disableGutters sx={{ height: { xs: HEADER.H_MOBILE } }}>
          <Box
            sx={{
              width: '100%',
              height: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              position: 'relative',
            }}
          >
            {isMobile() || isMd ? (
              <>
                <IconButton onClick={() => setMenuOpen(!menuOpen)}>
                  <Iconify icon={'eva:menu-2-outline'} sx={{ rotate: '180deg' }} />
                </IconButton>
                <Typography variant="h4" fontWeight={800}>
                  {title || ''}
                </Typography>
                <Avatar
                  onClick={handleClick}
                  aria-controls={open ? 'account-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                  sx={{ cursor: 'pointer', background: 'none' }}
                />
              </>
            ) : (
              <>
                <Logo sx={{ pl: 0 }} />
                <Box
                  sx={{
                    mx: 2,
                    textAlign: 'center',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <NavDesktop data={navConfig} />
                </Box>
                <Stack ml={1} direction={'row'} gap={1}>
                  <IconButton
                    onClick={() => {
                      const val = themeMode === 'light' ? 'dark' : 'light';
                      onChangeMode({
                        target: { value: val },
                      } as unknown as React.ChangeEvent<HTMLInputElement>);
                    }}
                  >
                    {themeMode === 'light' ? (
                      <NightlightOutlinedIcon sx={{ rotate: '-45deg' }} />
                    ) : (
                      <LightModeOutlinedIcon />
                    )}
                  </IconButton>
                  <Stack direction={'row'} gap={1} onClick={handleClick} sx={{ cursor: 'pointer' }}>
                    <Avatar />
                    <Stack
                      aria-controls={open ? 'account-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={open ? 'true' : undefined}
                    >
                      <Typography>{user?.userNm ?? '이름없음'}</Typography>
                      <Typography variant="caption" color={'GrayText'}>
                        {user?.lastLoginDt
                          ? `${moment(user?.lastLoginDt).format('YY/MM/DD HH:mm')} 접속`
                          : '최근 로그인 알 수 없음'}
                      </Typography>
                    </Stack>
                  </Stack>
                  {/* <Tooltip title="로그아웃">
                    <IconButton onClick={logout}>
                      <Logout fontSize="small" />
                    </IconButton>
                  </Tooltip> */}
                </Stack>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      {open && ( // 마이페이지 아이콘 popover
        <Menu
          anchorEl={anchorEl}
          id="account-menu"
          open={open}
          onClose={handleClose}
          onClick={handleClose}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.2))',
              mt: 1.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 20,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {/* <MenuItem onClick={() => navigate('/my/info')}>
            <ListItemIcon>
              <Iconify icon={'mingcute:user-setting-line'} />
            </ListItemIcon>
            내정보 수정
          </MenuItem>
          <Divider /> */}

          <MenuItem onClick={logout}>
            <ListItemIcon>
              <Logout fontSize="small" />
            </ListItemIcon>
            로그아웃
          </MenuItem>
        </Menu>
      )}

      <NavVertical openNav={menuOpen} onCloseNav={() => setMenuOpen(false)} />
    </>
  );
});

export default Header;
